import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const SEARCH_INPUT_DEBOUNCE_MS = 2000;

const useDebounce = (search: any, delay: any = SEARCH_INPUT_DEBOUNCE_MS, flushSignal?: any) => {
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const previousFlushSignal = useRef<any>(flushSignal);
  const [value, setValue] = useState<any>(search);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (previousFlushSignal.current !== flushSignal) {
      previousFlushSignal.current = flushSignal;
      setValue(search);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      setValue(search);
    }, delay);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search, delay, flushSignal]);

  return value;
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState<any>(window?.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window?.innerWidth <= breakpoint);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint]);

  return isMobile;
};

function useElementSize<T extends HTMLElement>() {
  const [node, setNode] = useState<T | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const ref = useCallback((element: T | null) => {
    setNode(element);
  }, []);

  useLayoutEffect(() => {
    if (!node) return;

    const updateDimensions = () => {
      const dimensions = node.getBoundingClientRect();
      setSize({ width: dimensions.width, height: dimensions.height });
    };

    updateDimensions();

    const observer = new ResizeObserver(updateDimensions);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [node]);

  return { ref, size };
}

export { SEARCH_INPUT_DEBOUNCE_MS, useDebounce, useIsMobile, useElementSize };
